import { useEffect, useState } from "react";
import { NumericFormat } from "react-number-format";
import { ChevronLeft, ChevronRight } from "react-feather";
import clsx from "clsx";
import BigNumber from "bignumber.js";
import { getAssociatedTokenAddressSync } from "@solana/spl-token";
import { useConnection } from "@solana/wallet-adapter-react";
import { Transaction } from "@solana/web3.js";
import useLanguage from "@axvdex/hooks/useLanguage";
import { selectWalletInfo } from "@axvdex/state/wallet/walletSelectors";
import { useAppDispatch, useAppSelector } from "@axvdex/state";
import { Bidder, OutbidSale, Sale } from "@axvdex/types/solana/outbid";
import Button from "@axvdex/components/common/Button";
import CustomLoader from "@axvdex/components/common/CustomLoader";
import CustomModal from "@axvdex/components/common/CustomModal";
import styles from "@axvdex/styles/Outbid.module.scss";
import { findBid, findBidder, findTransferAuthority } from "@axvdex/hooks/solana/utils/DeriveOutbidPDA";
import { createRedeemAssetsFromVestingInstruction, errorFromCode } from "@axvdex/solana/outbidAPI/js/generated";
import { getOrCreateAssociatedTokenAccount } from "@axvdex/hooks/solana/useTestToken";
import { sendToast } from "@axvdex/state/wallet/walletSlice";
import { processError } from "@axvdex/hooks/solana/utils/client";

export type SaleRedeemSectionProps = {
  selectedSale: Sale | null;
  outbidSale: OutbidSale;
  bidderInfo: Bidder | null;
};

const SaleRedeemSection = ({ outbidSale, selectedSale, bidderInfo }: SaleRedeemSectionProps) => {
  const { i18 } = useLanguage();
  const walletInfo = useAppSelector(selectWalletInfo);
  const [NftModalOpen, setNFTModalOpen] = useState(false);
  const [loadingVesting, setLoadingVesting] = useState(false);
  const [loadingNFT, setLoadingNFT] = useState(false);
  const [isTransactionLoading, setIsTransactionLoading] = useState(false);
  const { connection } = useConnection();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (selectedSale && walletInfo.isConnected) {
      checkVestingRedeemable();
      checkNFTRedeemable();
    }
  }, [selectedSale, walletInfo, bidderInfo]);

  const [vestingRedeemable, setVestingRedeemable] = useState(null);

  const checkVestingRedeemable = async () => {
    if (!bidderInfo) return;
    setLoadingVesting(true);
    let redeemable = false;
    for (const bid of bidderInfo.bids) {
      if (!bid.redeemed) {
        redeemable = true;
      }
    }
    setVestingRedeemable(redeemable);
    setLoadingVesting(false);
  };

  const [nftRedeemable, setNFTRedeemable] = useState(null);
  const [NFTdetails, setNFTDetails] = useState(null);
  // const [nftImage, setNFTImage] = useState(null);

  const checkNFTRedeemable = async () => {
    setLoadingNFT(true);
    const index = selectedSale.topBidders.findIndex(
      topBidder => topBidder.address.toBase58() === walletInfo.walletAddress
    );
    if (-1 !== index) {
      try {
        // TODO: Convert to Solana
        // const res = await rpcClientQuerySmartContractWrapper(outbidSale.config.cw721_addr, {
        //   all_nft_info: { token_id: selectedSale.id + "-" + (index + 1) },
        // });
        // setNFTRedeemable(false);
        // setNFTDetails(res);
      } catch (e) {
        console.log(e);
        if (e.message.includes("not found")) setNFTRedeemable(true);
        setNFTDetails(null);
      }
    } else {
      setNFTRedeemable(false);
      setNFTDetails(null);
    }
    setLoadingNFT(false);
  };

  const vestingRedeemText = () => {
    // Question: is there a `totalTickets` value that we can use to calculate the amount of tickets? or is it boughtTicketsAmount?
    const vestingAssetAmount = BigNumber(bidderInfo.boughtTicketsAmount)
      .times(selectedSale.config.targetAllocation.ratioTicketToTargetAsset)
      .div(Math.pow(10, outbidSale.assets[selectedSale.config.targetAllocation.targetAssetMint.toBase58()].decimals))
      .decimalPlaces(6)
      .toString(10);

    if (new Date().getTime() < selectedSale.saleState.endVestingTimestamp * 1000) {
      return (
        <span>
          {i18("Vested until", "sales.redeem.vestedUntil")}
          <br />
          <br />
          {new Date(selectedSale.saleState.endVestingTimestamp * 1000).toLocaleString()}
          <br />
          <br />{" "}
          <NumericFormat
            className="ticketAmount"
            displayType="text"
            thousandSeparator=","
            decimalSeparator="."
            decimalScale={4}
            suffix={" " + outbidSale.assets[selectedSale.config.targetAllocation.targetAssetMint.toBase58()].symbol}
            value={vestingAssetAmount}
          />
        </span>
      );
    } else if (true === vestingRedeemable) {
      return (
        <span>
          {i18("Redeem Vesting", "sales.redeem.redeemVesting")}
          <br /> <br />{" "}
          <NumericFormat
            className="ticketAmount"
            displayType="text"
            thousandSeparator=","
            decimalSeparator="."
            decimalScale={4}
            suffix={" " + outbidSale.assets[selectedSale.config.targetAllocation.targetAssetMint.toBase58()].symbol}
            value={vestingAssetAmount}
          />
        </span>
      );
    } else if (false === vestingRedeemable) {
      return (
        <span>
          {i18("Already Redeemed", "sales.redeem.alreadyRedeemed")}
          <br /> <br />{" "}
          <NumericFormat
            className="ticketAmount"
            displayType="text"
            thousandSeparator=","
            decimalSeparator="."
            decimalScale={4}
            suffix={" " + outbidSale.assets[selectedSale.config.targetAllocation.targetAssetMint.toBase58()].symbol}
            value={vestingAssetAmount}
          />
        </span>
      );
    }
  };

  const handleRedeemVesting = async () => {
    setIsTransactionLoading(true);
    try {
      console.log("Redeeming vesting");
      if (!selectedSale.config.isProcessEnded) {
        throw new Error(`Waiting on ${outbidSale.name} to send the process end instruction.`);
      }
      const redeemAssetDestResult = await getOrCreateAssociatedTokenAccount(
        connection,
        selectedSale.config.targetAllocation.targetAssetMint,
        window.solanaClient.publicKey,
        window.solanaClient.sendTransaction
      );
      if (redeemAssetDestResult.ok === false) {
        throw new Error(redeemAssetDestResult.message);
      }
      const redeemAssetDest = redeemAssetDestResult.value;
      const [transferAuthority] = findTransferAuthority(selectedSale.saleAccount);
      const destTokenAccount = getAssociatedTokenAddressSync(
        selectedSale.config.targetAllocation.targetAssetMint,
        transferAuthority,
        true
      );
      // Find the first bid that is not redeemed
      const bidId = bidderInfo.bids.find(bid => !bid.redeemed).id.toNumber();
      const [bidderAccount] = findBidder(selectedSale.saleAccount, window.solanaClient.publicKey);
      const redeemIx = createRedeemAssetsFromVestingInstruction({
        saleAccount: selectedSale.saleAccount,
        transferAuthority: transferAuthority,
        bidder: bidderAccount,
        bidderAuthority: window.solanaClient.publicKey,
        redeemAssetDest: redeemAssetDest.address,
        redeemAssetSrc: destTokenAccount,
        redeemAssetMint: selectedSale.config.targetAllocation.targetAssetMint,
        bid: findBid(selectedSale.saleAccount, bidId)[0],
      });
      // Add the other bids
      for (const bid of bidderInfo.bids) {
        if (bid.redeemed || bid.id.toNumber() === bidId) {
          continue;
        }
        redeemIx.keys.push({
          pubkey: findBid(selectedSale.saleAccount, bid.id.toNumber())[0],
          isSigner: false,
          isWritable: true,
        });
      }
      // Send the transaction
      const tx = new Transaction();
      tx.add(redeemIx);
      try {
        const signature = await window.solanaClient.sendTransaction(tx, connection);
        await connection.confirmTransaction(signature, "finalized");
      } catch (error) {
        console.error(processError(error.message, errorFromCode));
        throw error;
      }
      setVestingRedeemable(false);
    } catch (e) {
      console.error(e);
      const msg = e.message || "Error redeeming vesting";
      console.log(msg);
      dispatch(sendToast({ type: "tx-fail", info: { msg: msg, toastID: "" + new Date().getTime() } }));
      console.log("toast sent");
    } finally {
      setIsTransactionLoading(false);
    }
  };

  const [isTransactionNFTLoading, setIsTransactionNFTLoading] = useState(false);
  const handleNFTMint = async () => {
    setIsTransactionNFTLoading(true);
    try {
      await window.client.execute(
        walletInfo.walletAddress,
        outbidSale.address,
        {
          mint_sale_top_bidder_nft: {
            sale_id: selectedSale.id,
          },
        },
        "auto"
      );
    } catch (e) {
      /* empty */
    }

    setNFTModalOpen(true);
    checkNFTRedeemable();
    setIsTransactionNFTLoading(false);
  };

  if (!bidderInfo || !bidderInfo.bids || !selectedSale) return null;
  return (
    <div style={{ marginBottom: "1em" }}>
      <h1>{i18("Redeem", "sales.redeem")}</h1>
      <div style={{ display: "flex", marginBottom: "1em", placeContent: "space-evenly", alignItems: "center" }}>
        {null !== vestingRedeemable && (
          <Button
            style={{ maxWidth: "15em", padding: "1em" }}
            btnColor="gradient"
            disabled={!vestingRedeemable}
            onClick={() => handleRedeemVesting()}
          >
            {(loadingVesting || isTransactionLoading) && <CustomLoader size="xs" />}
            {!loadingVesting && !isTransactionLoading && vestingRedeemText()}
          </Button>
        )}
        {null !== nftRedeemable && true === nftRedeemable && (
          <Button
            style={{ maxWidth: "15em", padding: "1em" }}
            btnColor="gradient"
            disabled={!nftRedeemable || isTransactionNFTLoading}
            onClick={() => handleNFTMint()}
          >
            {(loadingNFT || isTransactionNFTLoading) && <CustomLoader size="xs" />}
            {!loadingNFT && !isTransactionNFTLoading && true === nftRedeemable ? (
              <div>
                <span>{i18("You won a bonus NFT!", "sales.redeem.nftWon")}</span>
                <br />
                <br />
                <span>{i18("Redeem NFT", "sales.redeem.nftRedeem")}</span>
              </div>
            ) : (
              ""
            )}
          </Button>
        )}
        {
          NFTdetails && (
            // TODO:
            // outbidSale.nftSaleImages &&
            // outbidSale.nftSaleImages[NFTdetails.info.extension.attributes[0].value] &&
            // outbidSale.nftSaleImages[NFTdetails.info.extension.attributes[0].value][
            // NFTdetails.info.extension.attributes[1].value] && (
            <div
              style={{ padding: "1em", textAlign: "center", cursor: "pointer" }}
              onClick={() => setNFTModalOpen(true)}
            >
              <img
                style={{ maxWidth: "15em", padding: "1em" }}
                src={
                  outbidSale.nftSaleImages[NFTdetails.info.extension.attributes[0].value][
                  NFTdetails.info.extension.attributes[1].value
                  ][0]
                }
              ></img>
              <br />
              <span>
                {NFTdetails.info.extension.name.split("/")[1] + " | " + NFTdetails.info.extension.name.split("/")[2]}
              </span>
            </div>
          )
          /*)*/
        }
        {NFTdetails && (
          <NFTModal
            NftModalOpen={NftModalOpen}
            setNFTModalOpen={setNFTModalOpen}
            saleID={NFTdetails.info.extension.attributes[0].value}
            selectedSale={selectedSale}
            topNumber={NFTdetails.info.extension.attributes[1].value}
            outbidSale={outbidSale}
            bidderInfo={bidderInfo}
          />
        )}
      </div>
    </div>
  );
};

type NFTModalProps = {
  NftModalOpen: boolean;
  setNFTModalOpen: (open: boolean) => void;
  saleID: number;
  selectedSale: Sale;
  topNumber: number;
  outbidSale: OutbidSale;
  bidderInfo: Bidder;
};

export function NFTModal({
  NftModalOpen,
  setNFTModalOpen,
  saleID,
  selectedSale,
  topNumber,
  outbidSale,
  bidderInfo,
}: NFTModalProps) {
  const { i18 } = useLanguage();
  const [images, setImages] = useState([]);
  const [bidsWithIncentives, setBidsWithIncentives] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 1,
  });

  useEffect(() => {
    if (outbidSale.nftSaleImages && outbidSale.nftSaleImages[saleID] && outbidSale.nftSaleImages[saleID][topNumber]) {
      setImages(outbidSale.nftSaleImages[saleID][topNumber]);
      setPagination({
        pageIndex: 0,
        pageSize: 1,
      });
    }
  }, [NftModalOpen, outbidSale]);

  useEffect(() => {
    if (bidderInfo && selectedSale.specificBidIncentives) {
      const bidsWithIncentives = [];
      for (const bidid of bidderInfo.bidIds) {
        const bidIncentives = selectedSale.specificBidIncentives.get(bidid.toString());
        if (bidIncentives) {
          bidsWithIncentives.push(bidIncentives);
        }
      }
      setBidsWithIncentives(bidsWithIncentives);
    }
  }, [bidderInfo, selectedSale]);

  useEffect(() => {
    if (NftModalOpen && bidderInfo && bidsWithIncentives.length > 0) {
      getNftDetails();
    }
  }, [NftModalOpen, outbidSale, bidsWithIncentives]);

  const getNftDetails = async () => {
    const images = [];
    for (const bid of bidsWithIncentives) {
      // TODO: Convert to Solana
      console.log("User has a bid with incentives", bid);
      // for (const incentive of bid.incentives) {
      //   if (incentive.nft) {
      //     const res = await rpcClientQuerySmartContractWrapper(incentive.nft.contract_addr, {
      //       all_nft_info: { token_id: incentive.nft.token_id },
      //     });
      //
      //     if (res?.info?.extension.image) images.push(res.info.extension.image);
      //   }
      // }
    }
    setImages(existingImages => [...existingImages, ...images]);
  };

  const paginationBulletsButtons = Array.from({ length: images.length }, (_, i) => (
    <li key={i}>
      <button
        className={clsx("btnTablePaginationBullet", i === pagination.pageIndex && "isCurrent")}
        onClick={() =>
          setPagination({
            ...pagination,
            pageIndex: i,
          })
        }
        title={`${i18("Go to page", "global.pagination.goToPage")} ${i + 1}`}
      />
    </li>
  ));

  if (!outbidSale.nftSaleImages[saleID]) return null;

  return (
    <>
      <CustomModal
        extraClassName={clsx(styles.sales, "sales")}
        isOpen={NftModalOpen}
        onClose={() => setNFTModalOpen(false)}
      >
        <section className="sectionModalHeader">
          <h2 className="h2">{"NFT"}</h2>
        </section>
        <div style={{ padding: ".5em", textAlign: "center" }}>
          <img style={{ maxWidth: "30em", padding: "1em" }} src={images[pagination.pageIndex]}></img>
          <br />
          <span>
            {pagination.pageIndex <= outbidSale.nftSaleImages[saleID][topNumber].length - 1
              ? "Sale " + saleID + " | " + "Top " + (topNumber + 1)
              : "Bid Reward"}
          </span>
        </div>
        {images.length > 1 && (
          <section
            className="statisticsGridItemsSectionTablePagination"
            style={{ paddingTop: ".5rem", paddingBottom: "1.5rem" }}
          >
            <Button
              extraClassName="btnTablePagination previous"
              title={i18("Previous page", "global.pagination.previousPage")}
              btnVariant="icon"
              btnColor="dark-medium"
              icon={<ChevronLeft />}
              onClick={() => setPagination({ ...pagination, pageIndex: pagination.pageIndex - 1 })}
              disabled={0 === pagination.pageIndex}
            />

            <ul className="tablePaginationBulletBtnList">{paginationBulletsButtons.map(bullet => bullet)}</ul>

            <Button
              extraClassName="btnTablePagination next"
              title={i18("Next page", "global.pagination.nextPage")}
              btnVariant="icon"
              btnColor="dark-medium"
              icon={<ChevronRight />}
              onClick={() => setPagination({ ...pagination, pageIndex: pagination.pageIndex + 1 })}
              disabled={pagination.pageIndex === images.length - 1}
            />
          </section>
        )}
        <div className="buttonContainer">
          <Button
            style={{ marginLeft: "auto" }}
            btnColor="gradient"
            text={"Dismiss"}
            title={"Dismiss"}
            onClick={() => {
              setNFTModalOpen(false);
            }}
          />
        </div>
      </CustomModal>
    </>
  );
}

export default SaleRedeemSection;
